/**
 * R-71 — per-report-type provider coverage registry.
 *
 * Maps audit-doc provider names to orchestrator ids via PROVIDER_ID_ALIASES.
 * `custom-brief` and `executive-summary` carry no required providers.
 */
import type { ReportTypeId } from '../../config/reportTypes';

export interface ProviderCoverageSpec {
  required: readonly string[];
  recommended: readonly string[];
  /** required orchestrator id → fallback orchestrator ids (in priority order). */
  fallback: Readonly<Record<string, readonly string[]>>;
}

/**
 * Audit-framework / legacy doc names → live orchestrator ids.
 * Human-review: uncertain NREL entries default to solar in alias table only;
 * report specs list explicit nrel-solar / nrel-wind where needed.
 */
export const PROVIDER_ID_ALIASES: Readonly<Record<string, string>> = {
  NREL: 'nrel-solar',
  FEMA: 'fema-flood',
  HIFLD: 'hifld-grid',
  'eia-utility': 'eia-electricity',
  'usfws-nwi': 'usfws-env',
  'airnow-aq': 'airnow-aqi',
  'usgs-water': 'wri-aqueduct',
  'sentiment-scan': 'public-sentiment',
  'ai-research': 'site-research',
  'peeringdb': 'peeringdb-ix',
  'open-meteo-climate': 'open-meteo',
  'usgs-seismic-hazard': 'usgs-seismic',
};

export function normalizeProviderId(providerId: string): string {
  return PROVIDER_ID_ALIASES[providerId] ?? providerId;
}

export const PROVIDER_COVERAGE: Record<ReportTypeId, ProviderCoverageSpec> = {
  feasibility: {
    required: ['open-meteo', 'hifld-grid', 'usgs-seismic', 'eia-electricity', 'peeringdb-ix'],
    recommended: ['fema-flood', 'usfws-env', 'airnow-aqi', 'nrel-solar', 'nrel-wind', 'nrc-reactors', 'site-research'],
    fallback: {
      'hifld-grid': ['eia-electricity'],
      'peeringdb-ix': ['hifld-grid'],
    },
  },
  environmental: {
    required: ['usfws-env', 'open-meteo', 'airnow-aqi'],
    recommended: ['fema-flood', 'hifld-grid', 'usgs-seismic', 'peeringdb-ix'],
    fallback: {
      'usfws-env': ['fema-flood'],
    },
  },
  'test-fit': {
    required: ['usgs-seismic', 'regrid-parcels'],
    recommended: ['fema-flood'],
    fallback: {},
  },
  'power-availability': {
    required: ['hifld-grid', 'eia-electricity', 'nrc-reactors'],
    recommended: ['nrel-solar', 'nrel-wind', 'peeringdb-ix'],
    fallback: {
      'nrc-reactors': ['hifld-grid'],
    },
  },
  'water-resource': {
    required: ['wri-aqueduct', 'open-meteo', 'usfws-env'],
    recommended: ['fema-flood'],
    fallback: {
      'wri-aqueduct': ['open-meteo'],
      // Review S4 (2026-09-05) — same fallback `environmental` already uses:
      // water is required of every site (P1.2), and without it one transient
      // USFWS failure left GO unreachable until a bootstrap re-run.
      'usfws-env': ['fema-flood'],
    },
  },
  interconnection: {
    required: ['peeringdb-ix', 'hifld-grid'],
    recommended: ['eia-electricity'],
    fallback: {
      'peeringdb-ix': ['hifld-grid'],
    },
  },
  'supply-chain-risk': {
    required: ['site-research', 'hifld-grid'],
    recommended: ['eia-electricity'],
    fallback: {},
  },
  'public-sentiment': {
    // NEVER-HARD-BLOCK (golden-pass 2026-06-27, validated live): a legitimately-
    // quiet site (no local media / docket / council discourse — e.g. rural
    // Redhead) yields a baseline sentiment section, which previously HARD-BLOCKED
    // the public-sentiment report at dispatch ("Provider coverage gate blocked
    // dispatch: Public sentiment scan"). That is backwards — "low public
    // discourse" IS the finding, and the three-layer (site/state/country) scan +
    // the honest "insufficient data" fallback handle the quiet case. The sentiment
    // SCAN itself is demoted to recommended (WARNS, never blocks); site-research
    // stays required for an evidentiary floor. (This mirrors the same change in
    // config/reportRequiredProviders.ts — the dispatch gate reads THIS registry.)
    // PSR tier-1 deep fetchers (census/fcc/news/…) stay recommended best-effort
    // enrichment.
    required: ['site-research'],
    recommended: ['public-sentiment', 'census-acs', 'fcc', 'news-aggregator', 'bls', 'local-public-data', 'local-gis'],
    fallback: {},
  },
  'custom-brief': {
    required: [],
    recommended: ['hifld-grid', 'site-research', 'open-meteo'],
    fallback: {},
  },
  'executive-summary': {
    required: [],
    recommended: ['hifld-grid', 'eia-electricity', 'site-research', 'open-meteo'],
    fallback: {},
  },
  // FIN-1 (wf w7nuyquwd) — Financial Viability Report. The body is computed
  // from the deterministic project-finance engine, not provider data; no
  // required providers. Electricity / grid pricing context is recommended.
  financial: {
    required: [],
    recommended: ['eia-electricity', 'hifld-grid', 'site-research'],
    fallback: {},
  },
};

export function getProviderCoverageSpec(reportType: ReportTypeId): ProviderCoverageSpec {
  return PROVIDER_COVERAGE[reportType];
}
